import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import type MaterialIcons from "@expo/vector-icons/MaterialIcons";
import Icon from "@expo/vector-icons/MaterialIcons";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import Box from "@memorang/ui/src/components/Box";
import { Image } from "expo-image";
import { useTranslation } from "react-i18next";
import { List, Text } from "react-native-paper";

type IconProps = {
	size?: number;
	color?: string;
};

type FeatureIcon =
	| { type: "material"; name: keyof typeof MaterialIcons.glyphMap }
	| { type: "community"; name: keyof typeof MaterialCommunityIcons.glyphMap }
	| { type: "image"; uri: string };

type FeatureItem = {
	id: string;
	label: string;
	description: string;
	icon: FeatureIcon;
};

// Feature icon component
const IconRenderer = ({
	icon,
	size = 16,
	color,
}: { icon: FeatureIcon } & IconProps) => {
	switch (icon.type) {
		case "material":
			return <Icon name={icon.name} size={size} color={color} />;
		case "community":
			return (
				<MaterialCommunityIcons name={icon.name} size={size} color={color} />
			);
		case "image":
			return (
				<Image
					source={{ uri: icon.uri }}
					style={{ width: size, height: size }}
				/>
			);
		default:
			return null;
	}
};

const features: FeatureItem[] = [
	{
		id: "confidence",
		label: "onboarding.features.confidence.label",
		description: "onboarding.features.confidence.description",
		icon: { type: "material", name: "sentiment-satisfied-alt" },
	},
	{
		id: "verified",
		label: "onboarding.features.verified.label",
		description: "onboarding.features.verified.description",
		icon: { type: "community", name: "check-decagram-outline" },
	},
	{
		id: "assistant",
		label: "onboarding.features.assistant.label",
		description: "onboarding.features.assistant.description",
		icon: {
			type: "image",
			uri: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/partners/toefl/icons/toefl_ai.svg",
		},
	},
];

const OnboardingFeatures = () => {
	const theme = useAppTheme();
	const appFont = useAppStore((app) => app.app.font);
	const { t } = useTranslation();

	return (
		<>
			<Box gap={12}>
				{features.map((feature) => (
					<List.Item
						key={feature.id}
						titleNumberOfLines={2}
						style={{
							borderWidth: 1,
							borderColor: theme.colors.surfaceVariant,
							padding: 16,
							backgroundColor: theme.colors.elevation.level1,
							marginBottom: 8,
						}}
						contentStyle={{ gap: 2 }}
						left={() => (
							<Box
								style={{
									borderWidth: 1,
									borderColor: theme.colors.surfaceVariant,
									width: 32,
									height: 32,
									justifyContent: "center",
									alignItems: "center",
									backgroundColor: theme.colors.elevation.level3,
								}}
							>
								<IconRenderer
									icon={feature.icon}
									size={16}
									color={theme.colors.primary}
								/>
							</Box>
						)}
						title={t(feature.label)}
						titleStyle={{
							fontFamily: `${appFont}-semibold`,
							color: theme.colors.primary,
						}}
						description={t(feature.description)}
					/>
				))}
			</Box>

			<Text
				variant="bodyLarge"
				style={{
					color: theme.palette.text.secondary,
					textAlign: "center",
					marginTop: 16,
				}}
			>
				{t("onboarding.steps.features.bottomLabel")}
			</Text>
		</>
	);
};

export default OnboardingFeatures;
